import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Snackbar from '@mui/material/Snackbar';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import MainLayout from "../layout/main-layout/mainLayout";
import LazyComponents from "../services/lazy-components/lazyComponents";
import DashboardLayout from "../layout/dashboard-layout/dashboardLayout";
import { setSnackBarData } from "../services/slices/snackBarSlice";
import { setAuthTokenResponseData } from "../services/slices/authTokenSlice";
import { isValue } from "../utils/constants/constants";
import AddFlightShopForm from "../pages/dynamic/add-flight-shop/index";
import ViewDashboardTableau from "../pages/dynamic/view-dashboard-tableau/viewDashboardTableau";
import APIServices from "../services/api-services";
import PageLoader from "../layout/page-loader/pageloader"
import { clearAllReduxStatesnew } from "../utils/common-hooks/clearState";
import { persistor } from "../store/store";
import CustomModal from "../components/common/modal/customModal";
import { URLExpired, userNotFound } from "../utils/helpers/helper";
import NotFoundPage from "../pages/static/accessdenied/accessdenied";
import { DASHBOARD_RESTRICTED_USERS } from "../redux/services/config";

const PageRouter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { fetchUserDetails, loginWithLink } = APIServices();
  const { authTokenResponseData } = useSelector((state) => state.authToken);
  const urlParams = useMemo(() => (new URLSearchParams(location.search)), [location.search]);
  const searchParams = new URLSearchParams(decodeURIComponent(location.search.replace(/&amp;/g, "&")));
  const type = searchParams.get("type");
  const jwt = "";
  // One-time login code from a BDE "login as", magic or share link
  const loginCode = urlParams.get('code');
  const { userdetails } = useSelector(state => (state.userDetails))
  const { snackBarData } = useSelector((state) => state?.snackBar)
  const [open, setOpen] = useState(false);
  const getUserDetailController = useRef(null);
  const loginWithLinkController = useRef(null);
  // A login link is exchanged once; the effect below re-runs whenever auth state changes
  const handledLoginLink = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(isValue?.emptyString);
  const [jwtLoader, setJwtLoader] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(isValue?.emptyString)

  const bdeFsid = location?.pathname.includes("dashboard") ? location?.pathname.split("/")[2] : null

  useEffect(() => {
    setOpen(snackBarData !== "" ? true : false)
  }, [snackBarData])

  const handleClose = () => {
    dispatch(setSnackBarData(isValue?.emptyString))
    setOpen(false)
  }

  // `data` is the /auth/code-exchange response; the session itself was set as httpOnly cookies.
  // Only reached when no one is signed in (see handleGetToken).
  const spoofLogin = useCallback((data) => {
    if (data?.isSpoofToken !== true) {
      alert("Invalid Spoofing Attempt!")
    }
    dispatch(setAuthTokenResponseData({
      ...data,
      isAuthenticated: true,
      isImpersonated: true,
      isBdePanel: true,
      bdepanelType: type,
    }));
    fetchUserDetails(bdeFsid, getUserDetailController, () => { }, setError, undefined, true);
    setJwtLoader(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, type, bdeFsid]);



  const purgePersistor = () => {
    return new Promise((resolve) => {
      persistor.purge();
      resolve();
    });
  };

  const clearSessionStorage = () => {
    return new Promise((resolve) => {
      sessionStorage.clear();
      resolve();
    });
  };

  const clearLocalStorage = () => {
    return new Promise((resolve) => {
      localStorage.clear();
      resolve();
    });
  };

  const handleGetToken = async () => {
    // Redeeming a login code replaces the session cookies, so never run it on top of a signed-in session
    if (authTokenResponseData?.isAuthenticated) {
      alert("To spoof login, please logout of the client account, close the browser tab and retry")
      setJwtLoader(false)
      navigate(authTokenResponseData?.isDashboard === true || userdetails?.isDashboard === true ? "/quick_access" : "/shop_manager", { replace: true })
      return
    }
    const response = await loginWithLink(loginCode, loginWithLinkController, () => { }, () => { });
    if (response?.data?.userName) {
      spoofLogin(response?.data);
    } else if (typeof response === "string" && /expired/i.test(response)) {
      handleLogout(URLExpired)
    } else if (typeof response === "string" && response && !/not found/i.test(response)) {
      // Blocked, rate-limited, server or network errors: show the actual reason, not "User Not Found"
      handleLogout([{ modalHeading: "Sign-in Failed" }, { modalContent: response }])
    } else {
      handleLogout(userNotFound)
    }
    setJwtLoader(false)
  }

  const handleLogout = (content) => {
    setModalContent(content);
    setModalOpen(true);
    clearAllReduxStatesnew(dispatch);
    purgePersistor();
    clearSessionStorage();
    clearLocalStorage();
  }

  useEffect(() => {
    if (!jwt && !bdeFsid && type !== "magiclink" && !location?.pathname.includes("?jwt=") && location?.pathname === "/" && !!authTokenResponseData?.isAuthenticated) {
      if (authTokenResponseData?.isDashboard === true || userdetails?.isDashboard === true) {
        navigate("/quick_access")
      } else {
        navigate("/shop_manager")
      }
    }
    setTimeout(() => {
      // Only a real login link (one-time code) and only once per URL
      const linkKey = location.search;
      if (!loginCode || handledLoginLink.current === linkKey) return;
      handledLoginLink.current = linkKey;

      setJwtLoader(true);
      handleGetToken();
    }, 100); // Adjust the delay as needed
    // eslint-disable-next-line
  }, [authTokenResponseData]);

  const {
    LazyLogin,
    LazyMyProfile,
    LazyShopManager,
    LazyDashboardManager,
    LazyViewDashboard,
    LazyQuickAccess,
    LazyLegacyDashboard,
    LazyLegacyDetail

  } = LazyComponents();

  const action = (
    <>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  )
  return (
    <>
      {!!jwtLoader ? <PageLoader /> :
        <Routes>
          {!authTokenResponseData.isAuthenticated ? (
            <Route path="/" element={<MainLayout />}>
              {/* Static Pages */}
              <Route index element={<LazyLogin />} />
              <Route path="login" element={<LazyLogin />} />
            </Route>
          ) : (
            <Route path="/" element={<DashboardLayout />}>
              {/*Dynamic Pages*/}
              <Route path="shop_manager" element={<LazyShopManager />} />
              <Route path="dashboard_manager" element={<LazyDashboardManager />} />
              {!DASHBOARD_RESTRICTED_USERS.includes(authTokenResponseData?.userName) && <Route path="view_dashboard" element={<LazyViewDashboard />} />}
              <Route path="legacy_dashboard" element={<LazyLegacyDashboard />} />
              <Route path="legacy_detail" element={<LazyLegacyDetail />} />
              <Route path="quick_access" element={<LazyQuickAccess />} />
              {!userdetails?.userExpired && !DASHBOARD_RESTRICTED_USERS.includes(authTokenResponseData?.userName)  && <>
                <Route path="/view_dashboard/:shopid" element={<ViewDashboardTableau />} />
              </>}

              {!userdetails?.userExpired && <>
                <Route path="flightshop" element={<AddFlightShopForm />} />
                <Route path="flightshop/:shopid" element={<AddFlightShopForm />} />
              </>}

              <Route path="myprofile" element={<LazyMyProfile />} />
            </Route>
          )}
          {!authTokenResponseData.isAuthenticated ? (
            <Route path="/*" element={<Navigate to="/" />} />
          ) : (
            <>
              <Route path="/*" element={<Navigate to={`/shop_manager${authTokenResponseData?.jwt ? `?jwt=${authTokenResponseData?.jwt}` : ''}`} />} />
            </>
          )}
          <Route path="/page-loader" element={<PageLoader />} />
          <Route path="/access-denied" element={<NotFoundPage />} />

        </Routes>
      }
      {snackBarData&& snackBarData!== ""&& <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        message={snackBarData}
        action={action}
      />}
      <CustomModal
        open={modalOpen}
        handleClose={() => setModalOpen(!modalOpen)}
        lable={{ labelOne: "Cancel", labelTwo: "Ok" }}
        cancellable={false}
        content={modalContent}
        handleClickOk={() => {
          setModalOpen(!modalOpen);
        }}
      />
    </>
  );
};

export default PageRouter;
